import { useRef, useState } from "react"
import { Upload, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { cn } from "@/lib/utils"

interface ImageUploadProps {
  image: File | null
  imagePreview: string | null
  onImageChange: (file: File | null) => void
  onImagePreviewChange: (preview: string | null) => void
}

export const ImageUpload = ({
  image,
  imagePreview,
  onImageChange,
  onImagePreviewChange,
}: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const { toast } = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        onImageChange(file)
        const preview = URL.createObjectURL(file)
        onImagePreviewChange(preview)
        toast({
          title: "Imagem selecionada",
          description: "Sua imagem foi carregada com sucesso!",
        })
      } else {
        toast({
          title: "Erro",
          description: "Por favor, selecione apenas arquivos de imagem.",
          variant: "destructive",
        })
      }
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    
    const file = e.dataTransfer.files[0]
    if (file && file.type.startsWith('image/')) {
      onImageChange(file)
      const preview = URL.createObjectURL(file)
      onImagePreviewChange(preview)
      toast({
        title: "Imagem carregada",
        description: "Sua imagem foi carregada com sucesso!",
      })
    } else {
      toast({
        title: "Erro",
        description: "Por favor, arraste apenas arquivos de imagem.",
        variant: "destructive",
      })
    }
  }

  const removeImage = () => {
    onImageChange(null)
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview)
      onImagePreviewChange(null)
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    toast({
      title: "Imagem removida",
      description: "A imagem foi removida com sucesso.",
    })
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-2">Imagem</label>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-6 transition-colors",
          isDragging ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary/50",
          "cursor-pointer relative"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        
        {imagePreview ? (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Preview"
              className="w-full h-64 object-cover rounded-lg"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={(e) => {
                e.stopPropagation()
                removeImage()
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="mx-auto h-12 w-12 text-gray-400" />
            <div className="mt-4 flex text-sm leading-6 text-gray-600">
              <span className="relative rounded-md font-semibold text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2">
                Clique para upload
              </span>
              <p className="pl-1">ou arraste e solte</p>
            </div>
            <p className="text-xs leading-5 text-gray-600">
              PNG, JPG, GIF até 10MB
            </p>
          </div>
        )}
      </div>
    </div>
  )
}